import { Icon } from '@iconify/react';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import InputAdornment from '@mui/material/InputAdornment';
import LinearProgress from '@mui/material/LinearProgress';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useCallback, useEffect, useState } from 'react';
import { useHasMenu } from '../../../auth/access';
import {
  errorText,
  fmtDate,
  fyToDate,
  money,
  Range,
  VOUCHER_TYPES,
  VoucherRow,
  vouchersApi,
  VoucherStatus,
  VoucherType,
} from '../accounting.api';
import BooksHeader, { RangeFields } from '../components/BooksHeader';
import { useBooksCompany } from '../components/CompanySelect';
import VoucherEntryDialog from '../components/VoucherEntryDialog';
import VoucherViewer, { CancelledChip, TypeChip } from '../components/VoucherViewer';

/**
 * The voucher register: every voucher of the company in a range (the financial year to date
 * by default), by type, status or a search on number, reference and narration. A row opens
 * the voucher; "New voucher" opens Tally-style entry (F4–F7 pick the type inside it).
 */
export default function VouchersPage() {
  const { companies, companyId, setCompanyId, loaded } = useBooksCompany();
  const canCreate = useHasMenu('accounts.vouchers', 'CREATE');

  const [range, setRange] = useState<Range>(fyToDate());
  const [type, setType] = useState<VoucherType | ''>('');
  const [status, setStatus] = useState<VoucherStatus | ''>('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<VoucherRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [entering, setEntering] = useState<VoucherType | null>(null);
  const [viewing, setViewing] = useState<number | null>(null);

  // The search box waits for a pause in typing.
  useEffect(() => {
    const t = setTimeout(() => setQuery(q.trim()), 350);
    return () => clearTimeout(t);
  }, [q]);

  const load = useCallback(() => {
    if (!companyId || !range.from || !range.to) return;
    setLoading(true);
    setError(null);
    vouchersApi
      .list(companyId, { ...range, type, status, q: query })
      .then(setRows)
      .catch((e) => {
        setRows([]);
        setError(errorText(e, 'Could not load vouchers', 'ACCOUNTING_VOUCHERS_FAILED'));
      })
      .finally(() => setLoading(false));
  }, [companyId, range, type, status, query]);

  useEffect(load, [load]);

  const posted = rows.filter((r) => r.status === 'POSTED');
  const total = posted.reduce((s, r) => s + Number(r.totalAmount), 0);

  return (
    <Stack gap={3}>
      <BooksHeader
        title='VOUCHERS'
        companies={companies}
        companyId={companyId}
        onCompany={setCompanyId}
        loaded={loaded}
        action={
          canCreate && (
            <Button variant='contained' startIcon={<Icon icon='mdi:plus' />} disabled={!companyId} onClick={() => setEntering('PAYMENT')}>
              New voucher
            </Button>
          )
        }
      >
        <RangeFields value={range} onChange={setRange} />
        <TextField select size='small' label='Type' value={type} onChange={(e) => setType(e.target.value as VoucherType | '')} sx={{ minWidth: 130 }}>
          <MenuItem value=''>All types</MenuItem>
          {VOUCHER_TYPES.map((t) => (
            <MenuItem key={t.type} value={t.type}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size='small'
          label='Status'
          value={status}
          onChange={(e) => setStatus(e.target.value as VoucherStatus | '')}
          sx={{ minWidth: 130 }}
        >
          <MenuItem value=''>All</MenuItem>
          <MenuItem value='POSTED'>Posted</MenuItem>
          <MenuItem value='CANCELLED'>Cancelled</MenuItem>
        </TextField>
        <TextField
          size='small'
          placeholder='Number, reference, narration'
          value={q}
          onChange={(e) => setQ(e.target.value)}
          sx={{ minWidth: 240, flex: { xs: 1, md: 'none' } }}
          InputProps={{
            startAdornment: (
              <InputAdornment position='start'>
                <Icon icon='mdi:magnify' />
              </InputAdornment>
            ),
          }}
        />
      </BooksHeader>

      {loading && <LinearProgress />}
      {error && <Alert severity='error'>{error}</Alert>}

      {companyId && (
        <TableContainer sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          <Table size='small'>
            <TableHead>
              <TableRow>
                <TableCell>Date</TableCell>
                <TableCell>Voucher no.</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Particulars</TableCell>
                <TableCell align='right'>Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && rows.length === 0 && (
                <TableRow>
                  <TableCell {...({ colSpan: 5 } as object)}>
                    <Typography variant='body2' color='text.secondary'>
                      No vouchers in this range.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {rows.map((r) => {
                const cancelled = r.status === 'CANCELLED';
                return (
                  <TableRow
                    key={r.id}
                    hover
                    onClick={() => setViewing(r.id)}
                    sx={{ cursor: 'pointer', bgcolor: cancelled ? 'rgba(211, 47, 47, 0.04)' : undefined }}
                  >
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{fmtDate(r.date)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap', textDecoration: cancelled ? 'line-through' : undefined }}>{r.voucherNo}</TableCell>
                    <TableCell>
                      <Stack direction='row' gap={0.5} flexWrap='wrap'>
                        <TypeChip type={r.type} />
                        {cancelled && <CancelledChip />}
                      </Stack>
                    </TableCell>
                    <TableCell sx={{ minWidth: 200 }}>
                      <Typography variant='body2'>{r.particulars}</Typography>
                      {r.narration && (
                        <Typography variant='caption' color='text.secondary'>
                          {r.narration}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell
                      align='right'
                      sx={{ whiteSpace: 'nowrap', fontWeight: 600, color: cancelled ? 'text.disabled' : undefined, textDecoration: cancelled ? 'line-through' : undefined }}
                    >
                      {money(r.totalAmount)}
                    </TableCell>
                  </TableRow>
                );
              })}
              {posted.length > 0 && (
                <TableRow>
                  <TableCell {...({ colSpan: 4 } as object)} sx={{ fontWeight: 700 }}>
                    {posted.length} posted voucher{posted.length === 1 ? '' : 's'}
                    {rows.length > posted.length ? ` (${rows.length - posted.length} cancelled not counted)` : ''}
                  </TableCell>
                  <TableCell align='right' sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
                    {money(total)}
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <VoucherEntryDialog
        open={entering !== null}
        companyId={companyId}
        voucher={null}
        initialType={entering ?? 'PAYMENT'}
        onClose={() => setEntering(null)}
        onSaved={load}
      />

      <VoucherViewer companyId={companyId} voucherId={viewing} onClose={() => setViewing(null)} onChanged={load} />
    </Stack>
  );
}
